// src/pages/sessions/components/LiveSession/managers/BreakoutRoomManager.js
import { useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'sonner';
import sessionService from '@/services/sessionService';

// Helper to create versioned teacher state
const createTeacherState = (broadcastingTo = [], monitoringRooms = [], source = 'unknown') => ({
  broadcastingTo,
  monitoringRooms,
  timestamp: Date.now(),
  source // 'api', 'websocket', 'metadata'
});

export function useBreakoutRoomManager({
  sessionId,
  user,
  isTeacherUser,
  localParticipantIdentity,
  roomRef,
  roomConnected,
  wsConnected,
  wsSendMessage,
  addMessageHandler,
  updateMetadata,
  updateParticipantMetadata,
  virtualRooms,
  currentVirtualRoom,
  participantVirtualRooms,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  teacherMonitorSilentRooms, // NEW: Silent monitoring rooms
  loadVirtualRooms,
  setCurrentVirtualRoom,
  setTeacherBroadcastRooms,
  setTeacherMonitorRooms,
  setTeacherMonitorSilentRooms, // NEW: Silent monitoring rooms setter
  applyAllOnce,
  cleanupTracker,
  savedVirtualRoomIdRef,
  sessionSettings
}) {
  // CRITICAL: Track remote teacher states for students with versioning
  const [remoteTeacherStates, setRemoteTeacherStates] = useState({});
  
  // Refs for state tracking
  const broadcastStateLoadedRef = useRef(false);
  const loadingBroadcastStateRef = useRef(false);
  const mountedRef = useRef(true);
  const processingRoomChangeRef = useRef(false);
  const hasLoadedInitialAssignmentRef = useRef(false);
  const lastAssignmentTimeRef = useRef(0);
  const persistDebounceRef = useRef(null);
  const creatingIndividualRoomsRef = useRef(false);
  const metadataUpdateDebounceRef = useRef(null);
  const hasLoadedTeacherStatesRef = useRef(false);
  const hasLoadedTeacherStatesFromAPIRef = useRef(false);
  
  // Version tracking for broadcast state changes to prevent multiple applications
  const broadcastStateVersionRef = useRef(0);
  const lastProcessedBroadcastVersionRef = useRef(0);
  const broadcastUpdateTimeoutRef = useRef(null);
  
  // CRITICAL FIX: Load teacher broadcast states from API with versioning
  const loadTeacherBroadcastStatesFromAPI = useCallback(async () => {
    if (isTeacherUser || !sessionId || !mountedRef.current) {
      return;
    }
    
    const apiLoadAttemptTime = Date.now();
    
    try {
      console.log('[Student] Loading teacher broadcast states from API...');
      const response = await sessionService.getTeacherBroadcastState(sessionId);
      
      if (!mountedRef.current) return;
      
      if (response?.success && response?.data) {
        const apiData = response.data;
        const newStates = {};
        
        // Handle both single and multiple teacher formats
        if (apiData.broadcastingTo !== undefined) {
          // Single teacher format
          if (roomRef.current) {
            const participants = roomRef.current.remoteParticipants;
            participants.forEach((participant, identity) => {
              try {
                const metadata = JSON.parse(participant.metadata || '{}');
                if (metadata.role === 'teacher') {
                  newStates[identity] = createTeacherState(
                    apiData.broadcastingTo || [],
                    apiData.monitoringRooms || [],
                    'api'
                  );
                }
              } catch (e) {
                console.error('[Student] Error parsing participant metadata:', e);
              }
            });
          }
        } else {
          // Multiple teachers format
          Object.entries(apiData).forEach(([userId, state]) => {
            const livekitIdentity = `user-${userId}-session-${sessionId}`;
            newStates[livekitIdentity] = createTeacherState(
              state.broadcastingTo || [],
              state.monitoringRooms || [],
              'api'
            );
          });
        }
        
        if (Object.keys(newStates).length > 0) {
          console.log('[Student] Merging teacher states from API:', newStates);
          
          setRemoteTeacherStates(prev => {
            const merged = { ...prev };
            
            Object.entries(newStates).forEach(([teacherId, apiState]) => {
              const currentState = prev[teacherId];
              
              if (!currentState) {
                // No existing state - use API
                merged[teacherId] = apiState;
                console.log(`[Student] Using API state for new teacher ${teacherId}`);
              } else if (apiLoadAttemptTime - currentState.timestamp < 2000) {
                // Current state is very recent (< 2s old) - keep it
                console.log(`[Student] Keeping recent state for ${teacherId} (age: ${apiLoadAttemptTime - currentState.timestamp}ms)`);
              } else if (
                apiState.broadcastingTo.length === 0 && 
                apiState.monitoringRooms.length === 0 &&
                (currentState.broadcastingTo.length > 0 || currentState.monitoringRooms.length > 0)
              ) {
                // API has no data but current state does - keep current
                console.log(`[Student] Keeping existing state for ${teacherId} (API empty, current has data)`);
              } else {
                // Use API state as it's likely fresher
                merged[teacherId] = apiState;
                console.log(`[Student] Updating ${teacherId} with API state`);
              }
            });
            
            return merged;
          });
          
          // Apply subscriptions with instant mode
          setTimeout(() => {
            if (typeof applyAllOnce === 'function') {
              console.log('[Student] Applying subscriptions after API merge');
              applyAllOnce(true);
            }
          }, 100);
          
          hasLoadedTeacherStatesFromAPIRef.current = true;
        }
      }
    } catch (error) {
      console.error('[Student] Error loading teacher broadcast states from API:', error);
    }
  }, [isTeacherUser, sessionId, applyAllOnce, setRemoteTeacherStates, roomRef]);

  // Load teacher states from existing participants (fallback) with versioning
  const loadTeacherStatesForStudent = useCallback(() => {
    if (!roomRef.current || isTeacherUser || hasLoadedTeacherStatesRef.current) {
      console.log('Skipping teacher states load from participants:', { 
        hasRoom: !!roomRef.current, 
        isTeacher: isTeacherUser,
        alreadyLoaded: hasLoadedTeacherStatesRef.current 
      });
      return;
    }
    
    hasLoadedTeacherStatesRef.current = true;
    console.log('Student loading teacher states from existing participants (fallback)...');
    
    const participants = roomRef.current.remoteParticipants;
    const teacherStates = {};
    let foundTeachers = 0;
    
    participants.forEach((participant, identity) => {
      try {
        const metadata = JSON.parse(participant.metadata || '{}');
        if (metadata.role === 'teacher') {
          foundTeachers++;
          teacherStates[identity] = createTeacherState(
            metadata.broadcastingTo || [],
            metadata.monitoringRooms || [],
            'metadata'
          );
          
          console.log(`Loaded teacher state from metadata for ${identity}:`, teacherStates[identity]);
        }
      } catch (e) {
        console.error('Error parsing participant metadata for', identity, ':', e);
      }
    });
    
    if (foundTeachers > 0) {
      console.log(`Found ${foundTeachers} teachers from metadata, updating remoteTeacherStates`);
      setRemoteTeacherStates(teacherStates);
      
      // Force immediate subscription update
      setTimeout(() => {
        if (typeof applyAllOnce === 'function') {
          console.log('Applying subscriptions after loading teacher states from metadata');
          applyAllOnce(true);
        }
      }, 100);
    } else {
      console.log('No teachers found in room participants');
    }
  }, [roomRef, isTeacherUser, applyAllOnce]);

  // Process room change with metadata update
  const processRoomChange = useCallback(async (newRoomId, roomName) => {
    if (!mountedRef.current) return;
    
    const currentRoomId = savedVirtualRoomIdRef.current;
    
    if (newRoomId === currentRoomId) {
      console.log('Already in room', newRoomId, 'skipping update');
      return;
    }
    
    if (processingRoomChangeRef.current) {
      console.log('Already processing room change, skipping');
      return;
    }
    
    processingRoomChangeRef.current = true;
    
    try {
      console.log('Processing room change from', currentRoomId, 'to', newRoomId);
      
      // CRITICAL: Update the ref IMMEDIATELY
      savedVirtualRoomIdRef.current = newRoomId;
      lastAssignmentTimeRef.current = Date.now();
      
      // CRITICAL FIX: For students, reload teacher states when changing rooms
      if (!isTeacherUser && roomRef.current) {
        console.log('Student changing rooms - reloading teacher states');
        
        // First try to load from API (most reliable)
        hasLoadedTeacherStatesFromAPIRef.current = false;
        await loadTeacherBroadcastStatesFromAPI();
        
        // Also load from participants as backup
        const participants = roomRef.current.remoteParticipants;
        const teacherStates = {};
        
        participants.forEach((participant, identity) => {
          try {
            const metadata = JSON.parse(participant.metadata || '{}');
            if (metadata.role === 'teacher') {
              teacherStates[identity] = createTeacherState(
                metadata.broadcastingTo || [],
                metadata.monitoringRooms || [],
                'metadata'
              );
              
              console.log(`Reloaded teacher state for ${identity}:`, teacherStates[identity]);
            }
          } catch (e) {
            console.error('Error parsing participant metadata for', identity, ':', e);
          }
        });
        
        // Merge with existing state from API if available
        if (Object.keys(teacherStates).length > 0) {
          console.log('Updating remoteTeacherStates with data from participants');
          setRemoteTeacherStates(prev => ({ ...prev, ...teacherStates }));
        }
      }
      
      // Update local state
      let newRoom = null;
      if (newRoomId) {
        newRoom = virtualRooms.find(r => r.id === newRoomId);
        if (!newRoom) {
          const loadedRooms = await loadVirtualRooms();
          newRoom = loadedRooms?.find(r => r.id === newRoomId);
        }
      }
      
      setCurrentVirtualRoom(newRoom);
      
      // Update metadata if room is connected
      if (roomRef.current?.localParticipant && roomConnected) {
        const metadata = {
          role: isTeacherUser ? 'teacher' : 'student',
          userId: user?.id,
          displayName: user?.displayName || user?.email,
          institutionMemberId: user?.institutionMemberId,
          virtualRoomId: newRoomId,
          ...(isTeacherUser && {
            broadcastingTo: Array.from(teacherBroadcastRooms),
            monitoringRooms: Array.from(teacherMonitorRooms)
          })
        };
        
        await updateMetadata(roomRef.current, metadata);
      }
      
      // Send WebSocket notification
      if (wsConnected && localParticipantIdentity) {
        wsSendMessage({
          type: 'participant_room_change',
          participantId: localParticipantIdentity,
          roomId: newRoomId,
          sessionId: sessionId
        });
      }
      
      // Apply subscriptions with updated room - use instant mode
      console.log('Applying subscriptions after room change to:', newRoomId);
      if (typeof applyAllOnce === 'function') {
        applyAllOnce(true);
      }
      
      // Show notification for students
      if (!isTeacherUser) {
        if (newRoom) {
          toast.info(`You've been moved to ${newRoom.display_name || newRoom.room_name || roomName || 'a breakout room'}`);
        } else if (newRoomId === null && currentRoomId !== null) {
          toast.info("You've been moved to the main room");
        }
      }
    } catch (error) {
      console.error('Error processing room change:', error);
    } finally {
      processingRoomChangeRef.current = false;
    }
  }, [virtualRooms, loadVirtualRooms, setCurrentVirtualRoom, wsConnected, 
      localParticipantIdentity, wsSendMessage, sessionId, isTeacherUser, 
      user, teacherBroadcastRooms, teacherMonitorRooms, applyAllOnce, 
      updateMetadata, roomConnected, savedVirtualRoomIdRef, roomRef, 
      setRemoteTeacherStates, loadTeacherBroadcastStatesFromAPI]);

  // Load initial room assignment for students
  const loadInitialRoomAssignment = useCallback(async () => {
    if (isTeacherUser || !sessionId || !mountedRef.current || hasLoadedInitialAssignmentRef.current) {
      console.log('Skipping initial room assignment load:', { 
        isTeacherUser, 
        sessionId, 
        hasLoaded: hasLoadedInitialAssignmentRef.current 
      });
      return null;
    }
    
    // CRITICAL: Don't load if we already have a room assignment
    if (savedVirtualRoomIdRef.current !== null && savedVirtualRoomIdRef.current !== undefined) {
      console.log('Already have room assignment:', savedVirtualRoomIdRef.current, '- skipping API load');
      hasLoadedInitialAssignmentRef.current = true;
      return savedVirtualRoomIdRef.current;
    }
    
    // Check if we recently processed a room change (within last 2 seconds)
    const timeSinceLastAssignment = Date.now() - lastAssignmentTimeRef.current;
    if (timeSinceLastAssignment < 2000) {
      console.log('Recent room assignment detected, skipping API load');
      hasLoadedInitialAssignmentRef.current = true;
      return savedVirtualRoomIdRef.current;
    }
    
    hasLoadedInitialAssignmentRef.current = true;
    
    try {
      console.log('Loading initial room assignment for student from API');
      const assignmentRes = await sessionService.getUserRoomAssignment(sessionId);
      
      if (!mountedRef.current) return null;
      
      // CRITICAL: Don't overwrite if we got assigned while waiting for API response
      if (savedVirtualRoomIdRef.current !== null && savedVirtualRoomIdRef.current !== undefined) {
        console.log('Got room assignment while waiting for API:', savedVirtualRoomIdRef.current, '- ignoring API response');
        return savedVirtualRoomIdRef.current;
      }
      
      const roomId = assignmentRes?.success && assignmentRes?.data ? 
        assignmentRes.data.virtualRoomId : null;
      
      const roomName = assignmentRes?.data?.roomName;
      
      console.log('Initial room assignment from API:', { roomId, roomName });
      
      // Only update if we still don't have a room
      if (savedVirtualRoomIdRef.current === null || savedVirtualRoomIdRef.current === undefined) {
        savedVirtualRoomIdRef.current = roomId;
        lastAssignmentTimeRef.current = Date.now();
        
        // Load rooms and set current room
        const roomsRes = await sessionService.getBreakoutRooms(sessionId);
        const rooms = roomsRes?.success ? roomsRes.data : [];
        
        if (roomId && rooms) {
          const assignedRoom = rooms.find(r => r.id === roomId);
          if (assignedRoom) {
            setCurrentVirtualRoom(assignedRoom);
            console.log('Set current virtual room from API:', assignedRoom);
          }
        }
        
        // Apply subscriptions immediately with the correct room ID
        console.log('Applying subscriptions with initial room from API:', roomId);
        setTimeout(() => {
          if (typeof applyAllOnce === 'function') {
            applyAllOnce();
          }
        }, 100);
      }
      
      return savedVirtualRoomIdRef.current;
    } catch (error) {
      console.error('Error loading room assignment:', error);
      return savedVirtualRoomIdRef.current;
    }
  }, [sessionId, isTeacherUser, setCurrentVirtualRoom, applyAllOnce, savedVirtualRoomIdRef]);

  // Handle individual room creation (teacher only)
  const handleCreateIndividualRooms = useCallback(async () => {
    if (!isTeacherUser || !mountedRef.current) return;
    
    if (creatingIndividualRoomsRef.current) {
      console.log('Already creating individual rooms, ignoring duplicate call');
      return;
    }
    
    creatingIndividualRoomsRef.current = true;
    
    try {
      const participants = roomRef.current?.remoteParticipants ? 
        Array.from(roomRef.current.remoteParticipants.values()) : [];
      
      const participantsInMainRoom = participants.filter(p => {
        let metadata = {};
        try {
          if (p.metadata) metadata = JSON.parse(p.metadata);
        } catch (e) {}
        
        const isTeacherRole = metadata.role === 'teacher';
        const hasVirtualRoom = metadata.virtualRoomId || participantVirtualRooms[p.identity];
        
        return !isTeacherRole && !hasVirtualRoom;
      });
      
      if (participantsInMainRoom.length === 0) {
        toast.info('No participants in main room to create rooms for');
        return;
      }
      
      toast.info(`Creating individual rooms for ${participantsInMainRoom.length} participants...`);
      
      const response = await sessionService.createIndividualVirtualRooms(sessionId);
      
      if (response?.success) {
        await loadVirtualRooms();
        toast.success('Individual rooms created successfully');
      } else {
        throw new Error(response?.message || 'Failed to create individual rooms');
      }
    } catch (error) {
      console.error('Error creating individual rooms:', error);
      toast.error('Failed to create individual rooms: ' + error.message);
    } finally {
      setTimeout(() => {
        creatingIndividualRoomsRef.current = false;
      }, 1000);
    }
  }, [isTeacherUser, sessionId, participantVirtualRooms, loadVirtualRooms, roomRef]);

  // Auto-assign breakout rooms: when enabled, create an individual room for any
  // student who joins the session without already having one.
  useEffect(() => {
    if (!isTeacherUser || !roomRef.current || !sessionSettings?.autoAssignBreakoutRooms) return;

    const room = roomRef.current;

    const handleAutoAssign = (participant) => {
      try {
        const metadata = participant.metadata ? JSON.parse(participant.metadata) : {};
        // Skip teachers and anyone already in a room
        if (metadata.role === 'teacher' || metadata.virtualRoomId) return;
      } catch (_) {}

      // Debounce slightly so rapid multi-student joins don't fire simultaneously
      setTimeout(() => {
        if (!mountedRef.current) return;
        handleCreateIndividualRooms();
      }, 500);
    };

    room.on('participantConnected', handleAutoAssign);
    return () => {
      room.off('participantConnected', handleAutoAssign);
    };
  }, [isTeacherUser, roomRef, sessionSettings?.autoAssignBreakoutRooms, handleCreateIndividualRooms]);

  // Load teacher broadcast state
  const loadTeacherBroadcastState = useCallback(async () => {
    if (!isTeacherUser || !sessionId) {
      console.debug('Skipping loadTeacherBroadcastState: not teacher or no session');
      return { broadcastingTo: [], monitoringRooms: [] };
    }
    
    if (broadcastStateLoadedRef.current) {
      console.debug('Teacher broadcast state already loaded, skipping');
      return { broadcastingTo: [], monitoringRooms: [] };
    }
    
    if (loadingBroadcastStateRef.current) {
      console.debug('Teacher broadcast state already loading, skipping duplicate request');
      return { broadcastingTo: [], monitoringRooms: [] };
    }
    
    loadingBroadcastStateRef.current = true;
    console.debug('Loading teacher broadcast state...');
    
    try {
      const savedState = await sessionService.getTeacherBroadcastState(sessionId);
      
      if (!mountedRef.current) {
        loadingBroadcastStateRef.current = false;
        return { broadcastingTo: [], monitoringRooms: [] };
      }
      
      if (savedState?.success && savedState?.data) {
        const broadcastingTo = savedState.data.broadcastingTo || [];
        const monitoringRooms = savedState.data.monitoringRooms || [];
        
        setTeacherBroadcastRooms(new Set(broadcastingTo));
        setTeacherMonitorRooms(new Set(monitoringRooms));
        
        broadcastStateLoadedRef.current = true;
        console.debug('Teacher broadcast state loaded successfully:', { broadcastingTo, monitoringRooms });
        
        setTimeout(() => {
          if (typeof applyAllOnce === 'function') {
            applyAllOnce();
          }
        }, 500);
        
        return { broadcastingTo, monitoringRooms };
      }
    } catch (error) {
      if (error.code !== 'ERR_NETWORK' && error.code !== 'ERR_INSUFFICIENT_RESOURCES') {
        console.error('Failed to load broadcast state:', error);
      }
    } finally {
      loadingBroadcastStateRef.current = false;
    }
    
    return { broadcastingTo: [], monitoringRooms: [] };
  }, [sessionId, isTeacherUser, setTeacherBroadcastRooms, setTeacherMonitorRooms, applyAllOnce]);

  // Persist teacher broadcast state with increased debounce
  useEffect(() => {
    if (!roomConnected || !isTeacherUser || !sessionId || !mountedRef.current) {
      return;
    }

    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
      persistDebounceRef.current = null;
    }

    persistDebounceRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;
      
      persistDebounceRef.current = null;
      
      try {
        await sessionService.updateTeacherBroadcastState(sessionId, {
          broadcastingTo: Array.from(teacherBroadcastRooms),
          monitoringRooms: Array.from(teacherMonitorRooms)
        });
      } catch (error) {
        console.error('Failed to persist teacher broadcast state:', error);
      }
    }, 5000);

    return () => {
      if (persistDebounceRef.current) {
        clearTimeout(persistDebounceRef.current);
        persistDebounceRef.current = null;
      }
    };
  }, [roomConnected, isTeacherUser, sessionId, teacherBroadcastRooms, teacherMonitorRooms]);

  // CRITICAL FIX: Update teacher metadata when broadcast/monitor state changes with debouncing
  useEffect(() => {
    if (!roomConnected || !isTeacherUser || !mountedRef.current) return;
    if (!roomRef.current?.localParticipant || !updateParticipantMetadata) return;
    
    // Clear any existing debounce
    if (metadataUpdateDebounceRef.current) {
      clearTimeout(metadataUpdateDebounceRef.current);
    }
    
    // Debounce metadata update to avoid rapid changes
    metadataUpdateDebounceRef.current = setTimeout(async () => {
      if (!mountedRef.current || !roomRef.current?.localParticipant) return;
      
      const broadcastArray = teacherBroadcastRooms ? Array.from(teacherBroadcastRooms) : [];
      const monitorArray = teacherMonitorRooms ? Array.from(teacherMonitorRooms) : [];
      
      console.log('Updating teacher metadata with broadcast state:', {
        broadcastingTo: broadcastArray,
        monitoringRooms: monitorArray
      });
      
      // Update metadata multiple times to ensure it sticks
      for (let i = 0; i < 3; i++) {
        await updateParticipantMetadata({
          broadcastingTo: broadcastArray,
          monitoringRooms: monitorArray
        });
        
        if (i < 2) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    }, 300);
    
    // Also increment version for subscription updates
    broadcastStateVersionRef.current++;
    const currentVersion = broadcastStateVersionRef.current;
    
    console.log('Broadcast state changed, version:', currentVersion);
    
    // Clear any existing timeout
    if (broadcastUpdateTimeoutRef.current) {
      clearTimeout(broadcastUpdateTimeoutRef.current);
    }
    
    // Schedule subscription update with version check
    broadcastUpdateTimeoutRef.current = setTimeout(() => {
      if (currentVersion === broadcastStateVersionRef.current && 
          currentVersion !== lastProcessedBroadcastVersionRef.current) {
        
        lastProcessedBroadcastVersionRef.current = currentVersion;
        console.log('Applying subscriptions for broadcast change version:', currentVersion);
        if (typeof applyAllOnce === 'function') {
          applyAllOnce();
        }
      }
    }, 1000);
    
    return () => {
      if (broadcastUpdateTimeoutRef.current) {
        clearTimeout(broadcastUpdateTimeoutRef.current);
      }
      if (metadataUpdateDebounceRef.current) {
        clearTimeout(metadataUpdateDebounceRef.current);
      }
    };
  }, [teacherBroadcastRooms, teacherMonitorRooms, roomConnected, isTeacherUser, 
      roomRef, updateParticipantMetadata, applyAllOnce]);

  // CRITICAL: Load teacher states when room connects for students
  useEffect(() => {
    if (!roomConnected || isTeacherUser || !roomRef.current) return;
    
    // Reset the flags on reconnection
    hasLoadedTeacherStatesRef.current = false;
    hasLoadedTeacherStatesFromAPIRef.current = false;
    
    // Load teacher states after a short delay to ensure participants are populated
    const timer = setTimeout(async () => {
      // First try API (most reliable - source of truth)
      await loadTeacherBroadcastStatesFromAPI();
      // Then load from participants as backup
      loadTeacherStatesForStudent();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [roomConnected, isTeacherUser, loadTeacherStatesForStudent, loadTeacherBroadcastStatesFromAPI]);

  // WebSocket handlers for room assignments (students)
  useEffect(() => {
    if (!wsConnected || !sessionId || isTeacherUser || !localParticipantIdentity || !mountedRef.current) {
      return;
    }
    
    const handleRoomAssignmentUpdate = (data) => {
      if (!mountedRef.current) return;
      
      console.log('Room assignment WebSocket message received:', data);
      
      if (data.participantIdentity === localParticipantIdentity || 
          data.participantIdentity === `user-${user?.id}-session-${sessionId}`) {
        
        if (data.timestamp) {
          const messageTime = new Date(data.timestamp).getTime();
          const now = Date.now();
          if (now - messageTime > 5000) {
            console.log('Ignoring stale room assignment message (>5s old)');
            return;
          }
        }
        
        if (data.virtualRoomId !== undefined) {
          const newRoomId = data.virtualRoomId || null;
          const roomName = data.roomName;
          
          console.log('Processing room assignment update to:', newRoomId, 
                      'Current room:', savedVirtualRoomIdRef.current);
          
          if (newRoomId !== savedVirtualRoomIdRef.current) {
            lastAssignmentTimeRef.current = Date.now();
            processRoomChange(newRoomId, roomName);
          } else {
            console.log('Same room assignment, ignoring');
          }
        }
      }
    };
    
    const handleVirtualRoomDeleted = async (data) => {
      if (!mountedRef.current) return;
      
      console.log('Virtual room deleted:', data.roomId);
      const deletedRoomId = data.roomId;
      const currentRoomId = savedVirtualRoomIdRef.current;
      
      if (currentRoomId && Number(currentRoomId) === Number(deletedRoomId)) {
        console.log('My room was deleted, moving to main room');
        lastAssignmentTimeRef.current = Date.now();
        await processRoomChange(null, 'Main Room');
      }
      
      await loadVirtualRooms();
    };
    
    const unsubRoomAssignment = addMessageHandler('room_assignment_update', handleRoomAssignmentUpdate);
    const unsubRoomDeleted = addMessageHandler('virtual_room_deleted', handleVirtualRoomDeleted);
    const unsubParticipantAssigned = addMessageHandler('participant_assigned', handleRoomAssignmentUpdate);
    
    cleanupTracker.addSubscription(() => {
      unsubRoomAssignment();
      unsubRoomDeleted();
      unsubParticipantAssigned();
    });
    
    return () => {
      unsubRoomAssignment();
      unsubRoomDeleted();
      unsubParticipantAssigned();
    };
  }, [wsConnected, sessionId, isTeacherUser, localParticipantIdentity, 
      user?.id, addMessageHandler, cleanupTracker, processRoomChange, 
      loadVirtualRooms, savedVirtualRoomIdRef]);

  // CRITICAL FIX: WebSocket handlers with versioning
  useEffect(() => {
    if (!wsConnected || !sessionId || !mountedRef.current) {
      return;
    }
    
    const handleTeacherBroadcastUpdate = (data) => {
      if (!mountedRef.current) return;
      
      console.log('[WebSocket] Teacher broadcast update received:', data);
      
      const wsTeacherIdentity = data.teacherIdentity || data.participantIdentity;
      
      if (isTeacherUser && wsTeacherIdentity) {
        if (wsTeacherIdentity.includes(`user-${user?.id}-`)) {
          console.log('[WebSocket] Ignoring our own broadcast update');
          return;
        }
      }
      
      const joinedRooms = data.broadcastingToRooms || data.broadcastingTo || [];
      
      // CRITICAL FIX: Map WebSocket teacher identity to LiveKit format for students
      if (!isTeacherUser && wsTeacherIdentity) {
        const userIdMatch = wsTeacherIdentity.match(/^user-(\d+)-/);
        if (userIdMatch) {
          const userId = userIdMatch[1];
          const livekitTeacherIdentity = `user-${userId}-session-${sessionId}`;
          
          console.log(`[WebSocket] Mapping ${wsTeacherIdentity} to ${livekitTeacherIdentity}`);
          console.log('[WebSocket] Broadcasting to rooms:', joinedRooms);
          
          setRemoteTeacherStates(prev => {
            const currentState = prev[livekitTeacherIdentity];
            const newState = createTeacherState(
              joinedRooms,
              currentState?.monitoringRooms || [],
              'websocket'
            );
            
            const updated = {
              ...prev,
              [livekitTeacherIdentity]: newState
            };
            
            console.log('[WebSocket] Updated teacher state:', newState);
            
            // Check for notifications
            const myRoomId = savedVirtualRoomIdRef.current;
            if (myRoomId !== undefined && myRoomId !== null) {
              const wasInRoom = currentState?.broadcastingTo?.some(room => 
                Number(room) === Number(myRoomId));
              const isInRoom = joinedRooms.some(room => Number(room) === Number(myRoomId));
              
              if (!wasInRoom && isInRoom) {
                toast.info('Teacher has joined your room');
              } else if (wasInRoom && !isInRoom) {
                toast.info('Teacher has left your room');
              }
            }
            
            return updated;
          });
          
          // INSTANT application - WebSocket updates need immediate visibility
          console.log('[WebSocket] Applying subscriptions INSTANTLY');
          if (typeof applyAllOnce === 'function') {
            applyAllOnce(true);
          }
        }
      }
      
      // Teacher's own updates
      if (isTeacherUser) {
        if (Array.isArray(joinedRooms)) {
          const newJoinedRooms = new Set(joinedRooms.map(id => Number(id)));
          const currentRooms = Array.from(teacherBroadcastRooms).map(id => Number(id)).sort();
          const newRooms = Array.from(newJoinedRooms).sort();
          
          if (JSON.stringify(currentRooms) !== JSON.stringify(newRooms)) {
            setTeacherBroadcastRooms(newJoinedRooms);
          }
        }
      }
    };
    
    const handleTeacherMonitorUpdate = (data) => {
      if (!mountedRef.current) return;
      
      const wsTeacherIdentity = data.teacherIdentity || data.participantIdentity;
      
      if (isTeacherUser && wsTeacherIdentity) {
        if (wsTeacherIdentity.includes(`user-${user?.id}-`)) {
          console.log('[WebSocket] Ignoring our own monitor update');
          return;
        }
      }
      
      const monitoringRooms = data.monitoringRooms || [];
      
      if (!isTeacherUser && wsTeacherIdentity) {
        const userIdMatch = wsTeacherIdentity.match(/^user-(\d+)-/);
        if (userIdMatch) {
          const userId = userIdMatch[1];
          const livekitTeacherIdentity = `user-${userId}-session-${sessionId}`;
          
          console.log(`[WebSocket] Mapping monitor ${wsTeacherIdentity} to ${livekitTeacherIdentity}`);
          
          setRemoteTeacherStates(prev => {
            const currentState = prev[livekitTeacherIdentity];
            return {
              ...prev,
              [livekitTeacherIdentity]: createTeacherState(
                currentState?.broadcastingTo || [],
                monitoringRooms,
                'websocket'
              )
            };
          });
          
          console.log('[WebSocket] Applying subscriptions INSTANTLY');
          if (typeof applyAllOnce === 'function') {
            applyAllOnce(true);
          }
        }
      }
      
      if (isTeacherUser) {
        if (Array.isArray(monitoringRooms)) {
          const newMonitorRooms = new Set(monitoringRooms.map(id => Number(id)));
          const currentMonitor = Array.from(teacherMonitorRooms).map(id => Number(id)).sort();
          const newMonitor = Array.from(newMonitorRooms).sort();
          
          if (JSON.stringify(currentMonitor) !== JSON.stringify(newMonitor)) {
            setTeacherMonitorRooms(newMonitorRooms);
          }
        }
      }
    };
    
    const unsubBroadcast = addMessageHandler('teacher_broadcasting_update', handleTeacherBroadcastUpdate);
    const unsubMonitor = addMessageHandler('teacher_monitoring_update', handleTeacherMonitorUpdate);
    
    console.log('Registered teacher broadcast/monitor handlers for', isTeacherUser ? 'teacher' : 'student');
    
    cleanupTracker.addSubscription(() => {
      unsubBroadcast();
      unsubMonitor();
    });
    
    return () => {
      unsubBroadcast();
      unsubMonitor();
    };
  }, [wsConnected, sessionId, isTeacherUser, setTeacherBroadcastRooms, 
      setTeacherMonitorRooms, addMessageHandler, user?.id,
      teacherBroadcastRooms, teacherMonitorRooms, cleanupTracker, 
      savedVirtualRoomIdRef, applyAllOnce]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      broadcastStateLoadedRef.current = false;
      loadingBroadcastStateRef.current = false;
      processingRoomChangeRef.current = false;
      hasLoadedInitialAssignmentRef.current = false;
      hasLoadedTeacherStatesRef.current = false;
      hasLoadedTeacherStatesFromAPIRef.current = false;
      lastAssignmentTimeRef.current = 0;
      creatingIndividualRoomsRef.current = false;
      broadcastStateVersionRef.current = 0;
      lastProcessedBroadcastVersionRef.current = 0;
      
      if (persistDebounceRef.current) {
        clearTimeout(persistDebounceRef.current);
        persistDebounceRef.current = null;
      }
      if (broadcastUpdateTimeoutRef.current) {
        clearTimeout(broadcastUpdateTimeoutRef.current);
        broadcastUpdateTimeoutRef.current = null;
      }
      if (metadataUpdateDebounceRef.current) {
        clearTimeout(metadataUpdateDebounceRef.current);
        metadataUpdateDebounceRef.current = null;
      }
    };
  }, []);

  return {
    savedVirtualRoomIdRef,
    processRoomChange,
    loadInitialRoomAssignment,
    handleCreateIndividualRooms,
    loadTeacherBroadcastState,
    loadTeacherStatesForStudent,
    loadTeacherBroadcastStatesFromAPI,
    broadcastStateLoaded: broadcastStateLoadedRef.current,
    remoteTeacherStates,
    setRemoteTeacherStates
  };
}
